import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Check, 
  X, 
  ArrowRight,
  Loader2,
  CreditCard
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'
import { redirectToStripeCheckout } from '../utils/stripeCheckout'
import { toast } from '../utils/toast'
import { getPricingPlans, COMPARISON_FEATURES } from '../config/plans'
import { useDashboardData, useStripePortalSession } from '../services/dashboardQueries'
import { trackPlanSelected, trackBillingPeriodToggle, trackManageSubscription } from '../config/analyticsEvents'

const PricingPage = () => {
  const { user, getAuthToken } = useAuth()
  const navigate = useNavigate()
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [isYearly, setIsYearly] = useState(true)

  const { data: dashboardData } = useDashboardData(getAuthToken, user?.id, null, {
    enabled: !!user,
  })
  const userPlan = dashboardData?.plan || {}
  const isUserPro = userPlan?.name?.toLowerCase() === 'pro'

  const stripePortalMutation = useStripePortalSession(getAuthToken)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const plans = getPricingPlans(isYearly).map(plan => ({
    ...plan,
    features: plan.id === 'free' 
      ? [
          { name: '10 API calls per month', included: true },
          { name: 'Max 200KB image upload', included: true },
          { name: 'PNG & WebP formats', included: true },
          { name: 'Standard quality icons', included: true },
          { name: 'Basic email support', included: true },
          { name: 'Custom badges & effects', included: false },
          { name: 'Priority processing', included: false },
          { name: 'Advanced customization', included: false },
        ]
      : plan.features.map(f => ({ name: f, included: true })),
    isPromo: plan.popular
  }))

  const handleManageSubscription = async () => {
    trackManageSubscription()
    try {
      const result = await stripePortalMutation.mutateAsync()
      if (result?.url) {
        window.location.href = result.url
      } else {
        toast.error('Failed to open payment management portal.')
      }
    } catch {
      toast.error('Failed to open payment management portal. Please try again.')
    }
  }

  const handleUpgrade = async () => {
    if (isUserPro) {
      await handleManageSubscription()
      return
    }

    trackPlanSelected('pro', isYearly ? 'yearly' : 'monthly')

    if (!user) {
      navigate('/auth?plan=pro')
      return
    }

    setIsUpgrading(true)
    try {
      await redirectToStripeCheckout({
        userId: user.id,
        userEmail: user.email,
        isYearly,
      })
    } catch (error) {
      toast.error(error.message || 'Failed to start checkout. Please try again.')
      setIsUpgrading(false)
    }
  }

  const isProActionLoading = isUpgrading || stripePortalMutation.isPending

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Choose Your{' '}
              <span className="bg-gradient-to-r from-primary-500 to-emerald-500 bg-clip-text text-transparent">
                Perfect Plan
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8">
              Start free and upgrade when you're ready. No hidden fees, cancel anytime.
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4">
              <span className={`text-sm font-medium ${!isYearly ? 'text-slate-900' : 'text-slate-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => { setIsYearly(!isYearly); trackBillingPeriodToggle(!isYearly ? 'yearly' : 'monthly') }}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                  isYearly ? 'bg-primary-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    isYearly ? 'translate-x-6' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${isYearly ? 'text-slate-900' : 'text-slate-500'}`}>
                Yearly
              </span>
              {isYearly && (
                <span className="ml-2 px-2 py-1 text-xs font-semibold text-emerald-600 bg-emerald-100 rounded-full">
                  Save 40%
                </span>
              )}
            </div>
          </motion.div>
        </section>

        {/* Pricing Cards */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                {plan.popular && (
                  <>
                    <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-emerald-500 to-primary-500 rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-300"></div>
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10">
                      <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold text-white shadow-lg ${
                        isUserPro
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 shadow-emerald-500/30'
                          : 'bg-gradient-to-r from-red-500 to-orange-500 shadow-red-500/30'
                      }`}>
                        {isUserPro ? '✓ Your Current Plan' : '🔥 Limited Time Promo'}
                      </span>
                    </div>
                  </>
                )}
                
                <div className={`relative p-8 rounded-3xl h-full transition-all duration-300 ${
                  plan.popular 
                    ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-2xl' 
                    : 'bg-white text-slate-900 border border-slate-200 shadow-xl'
                }`}>
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold">{plan.name}</h3>
                    </div>
                    <p className={plan.popular ? 'text-slate-400' : 'text-slate-600'}>
                      {plan.description}
                    </p>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {plan.originalPrice && (
                        <span className="text-2xl font-medium text-slate-400 line-through">
                          {plan.originalPrice}
                        </span>
                      )}
                      <span className="text-5xl font-bold">{plan.price}</span>
                      {plan.period && (
                        <span className={plan.popular ? 'text-slate-400' : 'text-slate-600'}>
                          {plan.period}
                        </span>
                      )}
                    </div>
                    {plan.periodNote && (
                      <p className="text-emerald-400 text-sm font-semibold mt-2">
                        {plan.periodNote}
                      </p>
                    )}
                  </div>
                  
                  {plan.popular && (
                    <button
                      onClick={handleUpgrade}
                      disabled={isProActionLoading}
                      className={`w-full py-4 px-6 mb-8 text-center font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                        isUserPro
                          ? 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
                          : 'bg-gradient-to-r from-primary-500 to-emerald-500 text-white hover:from-primary-600 hover:to-emerald-600 shadow-primary-500/30'
                      }`}
                    >
                      {isProActionLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {stripePortalMutation.isPending ? 'Redirecting...' : 'Processing...'}
                        </>
                      ) : isUserPro ? (
                        <>
                          <CreditCard className="w-5 h-5" />
                          Manage in Stripe
                        </>
                      ) : (
                        <>
                          {plan.cta}
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                  
                  <ul className="space-y-4">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        {feature.included ? (
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${
                            plan.popular ? 'bg-emerald-500/20' : 'bg-green-500/10'
                          }`}>
                            <Check className={`w-4 h-4 ${plan.popular ? 'text-emerald-400' : 'text-green-500'}`} />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center mr-3 bg-slate-500/10">
                            <X className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                        <span className={feature.included 
                          ? (plan.popular ? 'text-slate-300' : 'text-slate-700')
                          : 'text-slate-400'
                        }>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Feature Comparison
            </h2>
            <p className="text-slate-600">
              See exactly what you get with each plan
            </p>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
              <div className="p-4 font-semibold text-slate-900">Feature</div>
              <div className="p-4 font-semibold text-slate-900 text-center">Free</div>
              <div className="p-4 font-semibold text-center bg-gradient-to-r from-primary-500 to-emerald-500 text-white">Pro</div>
            </div>
            
            {/* Table Body */}
            {COMPARISON_FEATURES.map((category) => (
              <div key={category.category}>
                <div className="grid grid-cols-3 bg-slate-50/50">
                  <div className="p-3 px-4 font-semibold text-slate-700 text-sm uppercase tracking-wide col-span-3">
                    {category.category}
                  </div>
                </div>
                {category.features.map((feature, index) => (
                  <div 
                    key={feature.name} 
                    className={`grid grid-cols-3 ${index < category.features.length - 1 ? 'border-b border-slate-100' : ''}`}
                  >
                    <div className="p-4 text-slate-700">{feature.name}</div>
                    <div className="p-4 text-center">
                      {typeof feature.free === 'boolean' ? (
                        feature.free ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-slate-300 mx-auto" />
                        )
                      ) : (
                        <span className="text-slate-600">{feature.free}</span>
                      )}
                    </div>
                    <div className="p-4 text-center bg-primary-50/30">
                      {typeof feature.pro === 'boolean' ? (
                        feature.pro ? (
                          <Check className="w-5 h-5 text-emerald-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-slate-300 mx-auto" />
                        )
                      ) : (
                        <span className="font-medium text-slate-900">{feature.pro}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </motion.div>
        </section>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="relative rounded-3xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-emerald-600"></div>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
            
            <div className="relative p-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {isUserPro ? "You're on the Pro Plan" : 'Ready to Scale Your App Icons?'}
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
                {isUserPro 
                  ? 'Manage your subscription, update payment methods, or view your billing history.'
                  : 'Join thousands of developers who trust EasyAppIcon for their icon generation needs.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {isUserPro ? (
                  <>
                    <button
                      onClick={handleManageSubscription}
                      disabled={stripePortalMutation.isPending}
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {stripePortalMutation.isPending ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Redirecting...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5" />
                          Manage Subscription
                        </>
                      )}
                    </button>
                    <Link
                      to="/dashboard?tab=billing"
                      className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
                    >
                      View Billing
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleUpgrade}
                      disabled={isUpgrading}
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary-600 font-semibold rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isUpgrading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Upgrade to Pro - {isYearly ? '$36/year' : '$5/month'}
                        </>
                      )}
                    </button>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
                    >
                      Learn More
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </section>
      </main>
      
      <Footer />
    </div>
  )
}

export default PricingPage
